import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';
import { EFFECTIVENESS_LABELS, COMPETENCY_SHORT } from '../utils/feedbackScales';
import { fetchJSON } from '../utils/api';
import { sectionTitle, sectionTitleBar } from '../components/workflow/constants';

// Post-training feedback form (docs/modules/ld-selfservice.md). Reached from
// the feedback-request email; only recorded attendees may answer. Four 1–5
// ratings, upserted into ld_feedback (re-submitting edits your answers).
//
// Carries the legacy Microsoft Forms survey's own wording
// and structure, minus the two questions the system can answer by itself:
//   - "I confirm that I attended …" → a read-only line stating the check-in
//     this page already required to let you in.
//   - "Your designation" → read from the staff record (frozen by the session's
//     completion snapshot) and filed with the answer, never
//     asked and not shown on the form.
const EFFECTIVENESS_QUESTIONS = [
  { key: 'programEffectiveness', label: 'Effectiveness of the Training Program' },
  { key: 'trainerEffectiveness', label: 'Effectiveness of the Trainer' },
];

const COMPETENCY_QUESTIONS = [
  { key: 'preCompetency', label: 'Pre-training' },
  { key: 'postCompetency', label: 'Post-Training' },
];

const ALL_QUESTIONS = [...EFFECTIVENESS_QUESTIONS, ...COMPETENCY_QUESTIONS];

// The legacy feedback sheet asked this as one combined free-text question —
// kept word for word so historical answers and new ones read the same.
const COMMENTS_LABEL =
  'Have you gained from this program? How will the concepts and skills learnt be helpful back at work? Any additional comments about the trainer/s?';
const COMMENTS_MAX = 5000;

// e.g. '18 August 2026 at 13:50' — the house date format (ld.md §1) plus time,
// which is the part that makes the check-in a fact rather than a claim. The
// API sends 'YYYY-MM-DDTHH:MM:SS' with no zone, so the clock half is sliced
// off the string rather than parsed into a Date and shifted.
const checkinMoment = (ts) => `${formatDate(ts)} at ${String(ts).slice(11, 16)}`;

// Five buttons carrying the scale's own wording (feedbackScales.js) rather than
// bare numbers — the legacy survey labelled every column and people answered
// against those words.
const ScaleRow = ({ label, value, labels, onPick }) => (
  <div>
    <p className="text-sm font-medium text-slate-800 mb-2">{label} *</p>
    <div className="grid grid-cols-5 gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onPick(n)}
          className={`px-1 py-2 rounded-lg border text-center transition-colors ${
            value === n
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'bg-white border-slate-300 text-slate-600 hover:border-blue-400'
          }`}
        >
          <span className="block text-sm font-semibold">{n}</span>
          <span className={`block text-[11px] leading-tight mt-0.5 ${value === n ? 'text-blue-50' : 'text-slate-500'}`}>
            {labels[n]}
          </span>
        </button>
      ))}
    </div>
  </div>
);

const FeedbackForm = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [me, setMe] = useState(null);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState('');
  const [hadPrior, setHadPrior] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // The form opens once the session has ended (same clock as the quiz);
  // `opensAt` is firm-local 'YYYY-MM-DDTHH:MM'.
  const [open, setOpen] = useState(true);
  const [opensAt, setOpensAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchJSON(`/api/ld/feedback-form/${sessionId}`);
      setSession(data.session);
      setMe(data.me || null);
      setOpen(data.open !== false);
      setOpensAt(data.opensAt || null);
      if (data.feedback) {
        const { comments: priorComments, ...priorScores } = data.feedback;
        setScores(priorScores);
        setComments(priorComments || '');
        setHadPrior(true);
      }
      setError(null);
    } catch (e) { setError(e.message); }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (ALL_QUESTIONS.some(q => !scores[q.key])) {
      setError('Please answer all four questions.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/feedback-form/${sessionId}`, {
        method: 'POST',
        body: JSON.stringify({ ...scores, comments }),
      });
      setSaved(true);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const pick = (key) => (n) => setScores(s => ({ ...s, [key]: n }));

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <h1 className="text-lg font-bold text-slate-900 mb-4">Attendance and Course Evaluation</h1>

        {/* What the survey used to ask as questions 1 and 2 — both are facts the
            system holds, so they are stated, not asked. */}
        {session && (
          <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm">
            <p className="font-medium text-slate-800">{session.courseTitle}</p>
            <p className="text-slate-500">
              {formatDate(session.date)}
              {session.startEndTime ? ` · ${session.startEndTime}` : ''}
              {session.trainerNames ? ` · Trainer: ${session.trainerNames}` : ''}
            </p>
            <p className="text-slate-600">
              Your attendance at this session is confirmed
              {me?.checkedInAt ? ` by your check-in on ${checkinMoment(me.checkedInAt)}` : ' by your check-in record'}.
            </p>
            {hadPrior && !saved && (
              <p className="text-xs text-blue-600">You are editing your previous answers.</p>
            )}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {saved ? (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            Thank you — your feedback has been recorded.
          </div>
        ) : session && !open ? (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
            The feedback form opens {opensAt && opensAt.slice(11) !== '00:00' ? `at ${opensAt.slice(11)} ` : ''}on {formatDate(session.date)}, once the session has ended.
          </div>
        ) : session && (
          <div className="space-y-8">
            {/* Course evaluation — the legacy survey titled this block with the
                course and its trainer, and rated the program and the trainer. */}
            <section className="space-y-5">
              <p className="text-sm font-semibold text-slate-900">
                {session.courseTitle}
                {session.trainerNames ? ` by ${session.trainerNames}` : ''}
              </p>
              {EFFECTIVENESS_QUESTIONS.map((q) => (
                <ScaleRow
                  key={q.key}
                  label={q.label}
                  value={scores[q.key]}
                  labels={EFFECTIVENESS_LABELS}
                  onPick={pick(q.key)}
                />
              ))}
            </section>

            <div>
              <p className="text-sm font-medium text-slate-800 mb-2">{COMMENTS_LABEL}</p>
              <textarea
                rows="4"
                value={comments}
                maxLength={COMMENTS_MAX}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Tell the L&D team what you took away from the session."
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">{comments.length} / {COMMENTS_MAX}</p>
            </div>

            {/* Competency survey — its own section in the legacy form; the
                instruction line is condensed here. */}
            <section className="space-y-5 border-t border-slate-100 pt-6">
              <div>
                <h2 className={sectionTitle}>
                  <span className={sectionTitleBar} aria-hidden="true" />
                  Competency Survey
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  For each skill, rate your competency before and after the training (1–5).
                </p>
              </div>
              {COMPETENCY_QUESTIONS.map((q) => (
                <ScaleRow
                  key={q.key}
                  label={q.label}
                  value={scores[q.key]}
                  labels={COMPETENCY_SHORT}
                  onPick={pick(q.key)}
                />
              ))}
            </section>

            <div className="flex justify-end pt-2">
              <button
                onClick={submit}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 border-t border-slate-100 pt-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
